import { Link, useLocation } from 'react-router-dom';
import { Building2, CircleHelp, FileText, History, Landmark, Settings, type LucideIcon } from 'lucide-react';
import { useI18n, type MessageKey } from '../i18n';
import { LogoMark } from '../components/Logo';
import { useCompany } from './company';
import { periodHref } from '../lib/periods';

interface SideItem {
  key: string;
  to: string;
  label: MessageKey;
  icon: LucideIcon | 'logo';
  active: boolean;
  count?: number;
}

const ICON = { size: 18, strokeWidth: 1.6, 'aria-hidden': true } as const;

/**
 * Left navigation on wide screens (2026-11-02, J: "style of the website's app picture, functions of today's app"):
 * the side bar of the mock-up (.mk-side) — icon + label, the active item on a quiet grey with an orange bar on its
 * left, Instellingen at the bottom — with the destinations the app really has. Phones keep the bottom tab bar.
 */
export function SideNav() {
  const { t } = useI18n();
  const { pathname } = useLocation();
  const { current, quarter } = useCompany();
  const q = current?.row?.quarter ?? quarter;
  const vatTo = current && q ? periodHref(current.entity.id, q) : '/historie';
  const open = current?.row?.openQuestions ?? 0;

  const items: SideItem[] = [
    { key: 'overview', to: '/', label: 'side.overview', icon: Landmark, active: pathname === '/' },
    { key: 'questions', to: '/vragen', label: 'side.questions', icon: CircleHelp, active: pathname.startsWith('/vragen'), count: open },
    // 资料 (2026-11-03, J): invoices and bank statements in one place — tabs Verkoop · Inkoop · Bank on the page.
    { key: 'documents', to: '/documenten', label: 'side.documents', icon: FileText, active: pathname.startsWith('/documenten') },
    { key: 'vat', to: vatTo, label: 'side.vat', icon: 'logo', active: pathname.startsWith('/aangiftes/') || pathname.startsWith('/kor/') },
    { key: 'history', to: '/historie', label: 'side.history', icon: History, active: pathname.startsWith('/historie') },
    { key: 'companies', to: '/instellingen/bedrijven', label: 'side.companies', icon: Building2, active: pathname.startsWith('/instellingen/bedrijven') },
  ];
  const settingsActive = pathname.startsWith('/instellingen') && !pathname.startsWith('/instellingen/bedrijven');

  return (
    <nav className="sidenav" aria-label={t('side.label')}>
      <div className="sidenav__inner">
        <ul className="sidenav__items">
          {items.map(({ key, ...it }) => (
            <li key={key}>
              <SideLink {...it} />
            </li>
          ))}
        </ul>
        <SideLink to="/instellingen/profiel" label="side.settings" icon={Settings} active={settingsActive} />
      </div>
    </nav>
  );
}

function SideLink({ to, label, icon, active, count }: Omit<SideItem, 'key'>) {
  const { t } = useI18n();
  const Icon = icon === 'logo' ? null : icon;
  return (
    <Link to={to} className="sidenav__link" aria-current={active ? 'page' : undefined}>
      <span className="sidenav__icon">{Icon ? <Icon {...ICON} /> : <LogoMark size={16} simplified />}</span>
      <span className="sidenav__label">{t(label)}</span>
      {count ? (
        <span className="sidenav__count">
          <span aria-hidden="true">{count}</span>
          <span className="visually-hidden">{t('side.openCount', { n: count })}</span>
        </span>
      ) : null}
    </Link>
  );
}
